<template lang="pug">
    label(
        class="_radio",
        :class="_class",
        :style="{ width, height }",
        v-on="__on")
        input(
            ref="_in",
            type="radio",
            :id="id",
            :name="name",
            :value="val",
            :required="required",
            :disabled="disabled",
            :inert="inert",
            :tabindex="inert ? -1 : tabindex",
            v-on="__on",
            @change="changed")
        span(
            class="_cbc",
            :class="{ ['_'+tone]: tone }",
            aria-hidden="true") &zwnj;
        span(
            v-if="$slots.default",
            class="_cbl")
            slot
</template>

<script>
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';

    /**
     * A stylized radio button control that supports all v-model behavior of the raw input control.
     */
    export default {
        name: 'ib-radio',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
            * Toggles intended value data type
            */
            value: {
                type: [
                    String,   // checked = set to this.val
                    Number,   // checked = set to this.val
                    Boolean,  // checked = set to true
                    Array     // checked = contains this.val
                ],
                default: undefined
            },
            val: [String,Number],
            /**
             * Appearance modifier: `frameless` is supported on the ios platform.
             */
            appearance: String,
            /**
             * Take focus when made visible.
             */
            autofocus: Boolean,
            /**
             * Semantic color class.
             */
            tone: String
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _inv: this.showvalid && !this.valid,
                    ['_e'+this.appearance]: this.appearance
                }
            }
        },

        watch: {
            val() {
                this.dirty = true;
                this._sync();
            },
        },

        methods: {
            /** Callback for check/uncheck */
            changed() {
                this.validate();
                this.input(this.$refs._in.checked);
            },

            /** Can be called externally to simulate a click */
            click() {
                this.input(true);
                this.$refs._in.focus();
            },

            /** Retrieve desired check state based on value */
            checked(value) {
                if (value instanceof Array) {
                    return value.indexOf(this.val) >= 0;
                } else if (typeof value == 'Boolean') {
                    return value;
                } else {
                    return (this.val == value);
                }
            },

            /** @private */
            _click(e) {
                this.click();
                e.preventDefault();
            },

            /** @private */
            _sync() {
                if (this.dirty) {
                    const input = this.$refs._in;
                    const checked = this.checked(this.ivalue);
                    if (input.checked != checked) {
                        input.checked = checked;
                        this.validate();
                    }
                    this.dirty = false;
                }
            },

            input(checked) {
                if (typeof this.ivalue == 'Boolean') {
                    this._set(checked);
                } else if (checked) {
                    this._set(this.ivalue instanceof Array ? [this.val] : this.val);
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._radio {
        > ._cbc {
            width: @radio-size;
            height: @radio-size;
            line-height: @radio-lh;
            color: @fg-accent;
            .radio-default();
        }
        &._eframeless > ._cbc {
            .radio-frameless();
        }
        > input:hover + ._cbc {
            .radio-hover();
        }
        &._fo > ._cbc {
            .radio-focus();
        }
        > input:checked + ._cbc {
            .radio-checked();
        }
        &._eframeless > input:checked + ._cbc {
            .radio-frameless-checked();
        }
    }
</style>

<docs>
    ```jsx
        new Vue({
            data() {
                return {
                    value: null
                };
            },
            template: `
                <ib-context>
                    <ib-form>
                        <p class="after-md">Food groups: {{value}}</p>
                        <p class="before-sm"><ib-radio name="group" v-model="value" val="milk">The milk group</ib-radio></p>
                        <p class="before-sm"><ib-radio name="group" v-model="value" val="meat">The meat group</ib-radio></p>
                        <p class="before-sm"><ib-radio name="group" v-model="value" val="fruit">The fruits and vegetables group</ib-radio></p>
                        <p class="before-sm"><ib-radio name="group" v-model="value" val="bread">The breads and cereals group</ib-radio></p>
                        <p class="before-sm"><ib-radio name="group" v-model="value" val="all">All of the above</ib-radio></p>
                    </ib-form>
                </ib-context>`
        });
    ```
</docs>
